import React, { useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useSparePurchaseOrders } from '../../spares/spare-purchase-orders/useData';
import { computePurchaseOrderTotals } from '../../spares/spare-purchase-orders/purchaseOrderTotals';
import { WidgetShell } from './shared';

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const SparesTopSuppliersBySpend: React.FC<{ limit?: number; embedded?: boolean; settings?: Record<string, unknown> }> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const { colors } = useTheme();
  const effectiveLimit = typeof settings?.limit === 'number' ? settings.limit : limit;
  const { data, isLoading } = useSparePurchaseOrders(undefined, 1, 200);

  const rows = useMemo(() => {
    const spendBySupplier = new Map<string, { name: string; amount: number }>();
    for (const po of data ?? []) {
      const supplier = po.supplier?.name || `Supplier #${po.supplierId}`;
      const current = spendBySupplier.get(supplier) ?? { name: supplier, amount: 0 };
      const totals = computePurchaseOrderTotals(po);
      current.amount += totals.grandTotalUsd ?? totals.grandTotal;
      spendBySupplier.set(supplier, current);
    }
    return Array.from(spendBySupplier.values()).sort((a, b) => b.amount - a.amount).slice(0, effectiveLimit);
  }, [data, effectiveLimit]);

  return (
    <WidgetShell
      title="Spares · Top Suppliers by Spend"
      embedded={embedded}
      viewAllPath="/spares/spare-purchase-orders"
      isLoading={isLoading}
      empty={!isLoading && rows.length === 0}
      emptyMessage="No purchase order spend yet."
    >
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.name} className="flex items-center justify-between rounded-lg border px-3 py-2">
            <span className={`text-sm truncate ${colors.text}`}>{row.name}</span>
            <span className={`text-sm font-semibold ${colors.text}`}>${money(row.amount)}</span>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
};

export default SparesTopSuppliersBySpend;
